import React from 'react';

import {useSelector} from '@xstate/react';
import useLabelTranslation from 'src/hooks/useLabelTranslation';

import type {ActorRefFrom} from 'xstate';
import type {IRsvpStatus} from '../../types/IRsvpStatus';
import {selectError, selectRsvpStatus} from '../../selectors/commonSelector';

import {rsvpMachine} from '../../machines/rsvpMachine';
import {RootMachineContext} from '../../machines/rootMachine';
import {SmallCard} from '../../lib/small-card/small-card';
import {MeetingDateTimeInfo} from '../meeting-date-time-info/meeting-date-time-info';
import {CardChildren} from '../../lib/card-children/card-children';

import {SimpleButtonDropdown} from '@spaced-out/ui-design-system/lib/components/ButtonDropdown';
import {Button} from '@spaced-out/ui-design-system/lib/components/Button';
import {InContextAlert} from '@spaced-out/ui-design-system/lib/components/InContextAlert';

import css from './rsvp-flow.css';


export const RsvpFlow = () => {
  const {t} = useLabelTranslation();
  const rootActorRef = RootMachineContext.useActorRef();
  const rsvpActor = rootActorRef.system.get('rsvpMachine') as ActorRefFrom<
    typeof rsvpMachine
  >;
  const rsvpState = useSelector(rsvpActor, (state) => state);
  const rsvpStatus = useSelector(rsvpActor, selectRsvpStatus);
  const error = useSelector(rsvpActor, (state) => selectError(state, t));

  const handleRsvp = (status: IRsvpStatus) => {
    rsvpActor.send({
      type: 'rsvp.change',
      status,
    });
  };

  const handleSaveToCalender = () => {
    rsvpActor.send({
      type: 'saveToCalender',
    });
  };

  const isRsvpLoading = rsvpState.matches('rsvpLoading');
  const isSaveCalenderLoading = rsvpState.matches('saveCalenderLoading');
  const {errorMessage, allowRetry, isError} = error;

  return (
    <SmallCard title="RSVP" icon="calendar">
      <MeetingDateTimeInfo />
      {isError && (
        <InContextAlert semantic="danger">
          {/* Note: already getting translated */}
          {/* eslint-disable-next-line @spaced-out/i18n/no-dynamic-labels */}
          {errorMessage}
        </InContextAlert>
      )}
      <CardChildren>
        <div className={css.rsvpButtons}>
          <SimpleButtonDropdown
            onOptionSelect={(option) => handleRsvp(option.key as IRsvpStatus)}
            options={[
              {
                key: 'yes',
                label: t('Accept', 'Accept'),
                iconLeft: 'check',
              },
              {
                key: 'no',
                label: t('Decline', 'Decline'),
                iconLeft: 'close',
              },
            ]}
            isLoading={isRsvpLoading}
            type="primary"
            selectedKeys={[rsvpStatus]}
            iconLeftName={
              rsvpStatus === 'yes'
                ? 'check'
                : rsvpStatus === 'no'
                ? 'close'
                : ''
            }
            disabled={!allowRetry}
          >
            {rsvpStatus === 'yes'
              ? t('Accepted', 'Accepted')
              : rsvpStatus === 'no'
              ? t('Declined', 'Declined')
              : t('RSVP', 'RSVP')}
          </SimpleButtonDropdown>

          <Button
            type="tertiary"
            onClick={handleSaveToCalender}
            size="medium"
            isLoading={isSaveCalenderLoading}
            disabled={!allowRetry}
          >
            {t('Save_Invite_to_Calendar', 'Save Invite to Calendar')}
          </Button>
        </div>
      </CardChildren>
    </SmallCard>
  );
};
